import * as ScreenOrientation from 'expo-screen-orientation';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  InteractionManager,
  Linking,
  Platform,
  View
} from 'react-native';
import WebView, { WebViewNavigation, WebViewProps } from 'react-native-webview';
import styled from 'styled-components/native';

import { useFocusEffect } from '@react-navigation/native';

import api from '../api/api';
import Colors from '../constants/Colors';
import { useUserContext } from '../context/UserContext';
import { cssWorkSansRegular } from '../dynamic-forms/plugins/form-elements/cssFont';
import accessibilityTags from '../../accessibilityTags';

const LoadingView = styled.View`
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 30px;
  margin-bottom: 30px;
`;

const DynamicHeightWebView: React.FC<
  WebViewProps & {
    height?: number;
    onResizeLoad?: (height: number) => void;
    onFieldClicked?: (top: number) => void;
  }
> = ({ onResizeLoad, onFieldClicked, ...props }) => {
  const webViewRef = useRef<WebView>(null!);
  const { accessToken } = useUserContext();
  const [height, setHeight] = useState(props.height || 0);
  const [loading, setLoading] = useState(true);
  const [isWebViewLoaded, setIsWebViewLoaded] = useState<boolean>(false);

  // This is a modified fix for issue where app crashes upon navigating to the screen, if animation is enable on Android
  // from: https://github.com/react-native-webview/react-native-webview/issues/811#issuecomment-1198953814
  useEffect(() => {
    InteractionManager.runAfterInteractions(() => {
      if (Platform.OS !== 'web' && !isWebViewLoaded) {
        setIsWebViewLoaded(true);
      }
    });
  }, [isWebViewLoaded]);

  // Script to load images asynchronously with authorization header
  // Report scroll height each time image loads to resize webview
  const webViewScript = `
    ${
      onFieldClicked &&
      `
        function sendInputPosition(el) {
          const rect = el.getBoundingClientRect();
          window.ReactNativeWebView.postMessage(JSON.stringify({type: 'focus', top: rect.top}));
        }
        document.querySelectorAll('input, textarea').forEach(function(el) {
          el.addEventListener('click', function() {
            sendInputPosition(el);
          });
        });
    `
    }
    const imageElements = document.querySelectorAll('img');
    const updateScrollHeight = function() {
      window.ReactNativeWebView.postMessage(JSON.stringify({type: 'scroll-height', scrollHeight: document.documentElement.scrollHeight}));
    };
    for (const imageElement of imageElements) {
      const url = imageElement.src;
      let reloadAttempt = 0;
      if (url.indexOf('api.peoplescience.health') === -1 && url.indexOf('localhost') === -1) continue;
      fetch(url, {headers: { '${
        api.environment.accessTokenKey
      }': 'Bearer ${accessToken}' }})
        .then(response => response.blob())
        .then(blob => {
          const objectUrl = URL.createObjectURL(blob);
          imageElement.src = objectUrl;
          imageElement.onload = () => {
            URL.revokeObjectUrl(objectUrl);
          } // garbage cleanup
          ${
            Platform.OS === 'android' &&
            `
              imageElement.onerror = () => {
                if (reloadAttempt !== 5){
                  reloadAttempt++;
                  imageElement.src = url + new Date().getTime();
                } else {
                imageElement.height = 20;
                imageElement.width = 20;
                updateScrollHeight();
                }
              }
            `
          }
        }).finally(() => {
          // update scroll height each time image loads
          // window.ReactNativeWebView.postMessage(JSON.stringify({type: 'message', message: 'loaded image'}));
          imageElement.style.display = 'block';
          setTimeout(updateScrollHeight, 500);
        });
     }
    setTimeout(updateScrollHeight, 500);
    true;
  `;
  return (
    <View>
      {loading && (
        <LoadingView
          accessibilityLabel={accessibilityTags.loadingID}
          testID={accessibilityTags.loadingID}
        >
          <ActivityIndicator size='large' color={Colors.primary} />
        </LoadingView>
      )}
      <WebView
        {...props}
        ref={webViewRef}
        style={{
          height: height,
          opacity: isWebViewLoaded || Platform.OS === 'web' ? 100 : 0
        }}
        automaticallyAdjustContentInsets={false}
        scrollEnabled={false}
        onMessage={event => {
          const data = JSON.parse(event.nativeEvent.data);
          if (data.type === 'scroll-height' && !props.height) {
            // console.log('onMessage setHeight', height);
            setHeight(parseInt(data.scrollHeight));
            onResizeLoad?.(parseInt(data.scrollHeight));
          } else if (data.type === 'focus') {
            onFieldClicked?.(data.top);
          } else {
            // console.log('data', data);
          }
        }}
        showsVerticalScrollIndicator={false}
        thirdPartyCookiesEnabled
        sharedCookiesEnabled
        mediaPlaybackRequiresUserAction={true}
        allowsInlineMediaPlayback={true}
        scalesPageToFit={false}
        javaScriptEnabled={true}
        injectedJavaScript={webViewScript}
        domStorageEnabled={props.domStorageEnabled}
        useWebKit={true}
        mixedContentMode='always'
        allowsFullscreenVideo={true}
        onLoadEnd={() => {
          if (loading) {
            setLoading(false);
          }
        }}
        onShouldStartLoadWithRequest={event => {
          if (event.navigationType === 'click') {
            Linking.openURL(event.url);
            return false;
          }
          return true;
        }}
      />
    </View>
  );
};

/**
 * Wrapper for React Native's WebView and browser iframe to display dynamic
 * html content. The component provides styling, load events, video playback fix
 * and auto-resize to html content.
 */
const HtmlWebView: React.FC<{
  html?: string;
  url?: string;
  height?: number;
  incognito?: boolean;
  onLoad?: () => void;
  onFieldClicked?: (top: number) => void;
  onNavigationStateChange?: ((event: WebViewNavigation) => void) &
    ((event: WebViewNavigation) => void);
  bodyColor?: string;
}> = ({
  html,
  url,
  bodyColor,
  incognito,
  height,
  onLoad,
  onNavigationStateChange,
  onFieldClicked
}) => {
  const ref: any = useRef();
  const [iframeHeight, setIframeHeight] = useState('0px');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (Platform.OS === 'web') {
      const frame: HTMLIFrameElement =
        document.getElementById('myFrame') || ref.current;
      addEventListener('resize', () => {
        if (frame.contentWindow !== null) {
          setIframeHeight(
            frame.contentWindow.document.body.scrollHeight + 24 + 'px'
          );
        }
      });
    }
  }, []);

  // Fix for video orientation
  // screens containing this component will set the orientation to default when in focus
  // then resets back to portrait when is not in focus
  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === 'ios') {
        ScreenOrientation.unlockAsync();
        return () => lockPortraitOrientation();
      }
    }, [])
  );

  const head = `
  <head>
    <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, height=auto" charset="UTF-8"/>
  </head>`;
  const webCss = `<style type="text/css">
  ${cssWorkSansRegular}
  iframe { width: 100%; }
  body {
    background-color: ${Colors.background};
    font-family: 'WorkSans-Regular';
    color: ${bodyColor ? bodyColor : Colors.primary};
    margin: 0px;
    height:auto;
  }
  h1 { ${Platform.OS === 'ios' ? 'letter-spacing: -2.5px;' : ''} }
  h2 { ${Platform.OS === 'ios' ? 'letter-spacing: -1.5px;' : ''} }
  h3 { ${Platform.OS === 'ios' ? 'letter-spacing: -1px;' : ''} }
  </style>`;

  const fixImgLink = () => {
    if (typeof html !== 'undefined') {
      if (Platform.OS === 'android') {
        return html
          .split('http://localhost')
          .join('http://10.0.2.2')
          .split('<img')
          .join(
            `<img style='display: none' onload="this.style.display = 'inline-block'"`
          );
      } else {
        return html
          .split('<img')
          .join(
            `<img style='display: none' onload="this.style.display = 'inline-block'"`
          );
      }
    }
    return '';
  };

  if (!url) {
    html = webCss + head + '<body>' + fixImgLink() + '</body>';
    //script to open urls in a new tab or native browser
    html += `<script>
  var links = document.getElementsByTagName('a');
  for (var i=0; i < links.length; i++){
    links[i].setAttribute('target', '_blank');
  } </script>`;
    // script to set the display for images that were not loaded
    html += `<script>
  document.querySelectorAll(".an1").forEach((img) => {
    img.style.display = "inline-block";
  }) </script>`;
  }
  return (
    <View>
      {Platform.OS === 'web' ? (
        <View>
          <iframe
            ref={ref}
            onLoad={() => {
              setIframeHeight(
                ref.current.contentWindow.document.body.scrollHeight + 24 + 'px'
              );
              if (loading) {
                setLoading(false);
              }
              onLoad?.();
            }}
            id='myFrame'
            srcDoc={html}
            src={url}
            width='100%'
            height={iframeHeight}
            scrolling='no'
            frameBorder='0'
          ></iframe>
          {loading && (
            <LoadingView
              accessibilityLabel={accessibilityTags.loadingID}
              testID={accessibilityTags.loadingID}
            >
              <ActivityIndicator size='large' color='#fff' />
            </LoadingView>
          )}
        </View>
      ) : (
        <DynamicHeightWebView
          height={height}
          incognito={incognito}
          onFieldClicked={onFieldClicked}
          domStorageEnabled={incognito ? false : true}
          source={{ html, uri: url }}
          onResizeLoad={() => onLoad?.()}
          onNavigationStateChange={onNavigationStateChange}
        />
      )}
    </View>
  );
};

export default HtmlWebView;

async function lockPortraitOrientation() {
  await ScreenOrientation.lockAsync(
    ScreenOrientation.OrientationLock.PORTRAIT_UP
  );
}
